// Per-tab, bounded evidence. Receiving/caching a payload never means it was installed.
const events = [];
const responseEvidence = new WeakMap();
let sequence = 0;
const token = value => typeof value === "string" && /^[A-Za-z0-9_.:-]{1,128}$/.test(value) ? value : "";
const date = value => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
const period = value => ["day", "evening"].includes(value) ? value : "";

function record(payload, outcome, extra = {}) {
  if (!payload || typeof payload !== "object") return;
  const metadata = responseEvidence.get(payload) || {};
  const event = { ...metadata, dispatch_date: date(payload.dispatch_date || payload.planning_summary?.dispatch_date) || metadata.dispatch_date,
    dispatch_period: period(payload.dispatch_period || payload.planning_summary?.dispatch_period) || metadata.dispatch_period,
    runtime_revision: token(payload.runtime_revision), ...extra,
    outcome, sequence: ++sequence, at: new Date().toISOString() };
  if (!event.dispatch_date || !event.dispatch_period) return;
  events.push(event);
  if (events.length > 12) events.shift();
}

export function recordPlanningRevisionResponse(payload, response, { url = "", actionName = "results_latest" } = {}) {
  if (!payload || typeof payload !== "object") return;
  let params;
  try { params = new URL(url, "http://grain.local").searchParams; } catch { params = new URLSearchParams(); }
  const result = payload.result_payload && typeof payload.result_payload === "object" ? payload.result_payload : payload;
  const metadata = { request_id: token(response?.headers?.get?.("X-Request-ID") || payload.request_id),
    action: token(actionName), dispatch_date: date(result.dispatch_date || payload.dispatch_date || params.get("dispatch_date")),
    dispatch_period: period(result.dispatch_period || payload.dispatch_period || params.get("dispatch_period")) || "day" };
  responseEvidence.set(result, metadata);
  record(result, "received");
}

export function recordPlanningRevisionApplied(payload, actionName, previousRevision) {
  record(payload, "applied", { action: token(actionName), previous_revision: token(previousRevision) });
}

export function recordPlanningRevisionIgnored(payload, reason) {
  record(payload, "ignored", { reason: token(reason) });
}

export function planningRevisionTimeline(dispatchDate, dispatchPeriod) {
  return events.filter(event => event.dispatch_date === dispatchDate && event.dispatch_period === dispatchPeriod)
    .map(event => ({ ...event }));
}

export function planningRevisionForScope(payload, dispatchDate, dispatchPeriod) {
  return (payload?.dispatch_date || payload?.planning_summary?.dispatch_date) === dispatchDate
    && (payload?.dispatch_period || payload?.planning_summary?.dispatch_period) === dispatchPeriod
    ? token(payload?.runtime_revision) : undefined;
}

export function normalizePlanningRevisionEvidence(value) {
  const result = {};
  for (const key of ["initial_runtime_revision", "expected_runtime_revision", "current_runtime_revision",
    "received_runtime_revision", "installed_runtime_revision", "request_id"]) {
    if (token(value[key])) result[key] = token(value[key]);
  }
  if (Number.isInteger(value.expected_assignment_version) && value.expected_assignment_version >= 1) {
    result.expected_assignment_version = value.expected_assignment_version;
  }
  if (date(value.dispatch_date)) result.dispatch_date = date(value.dispatch_date);
  if (period(value.dispatch_period)) result.dispatch_period = period(value.dispatch_period);
  result.events = (Array.isArray(value.events) ? value.events : []).slice(-12).flatMap(event => {
    if (!event || !["received", "applied", "ignored"].includes(event.outcome)) return [];
    const item = { outcome: event.outcome };
    for (const key of ["runtime_revision", "previous_revision", "request_id", "action", "reason", "at"]) {
      if (token(event[key])) item[key] = token(event[key]);
    }
    if (date(event.dispatch_date)) item.dispatch_date = event.dispatch_date;
    if (period(event.dispatch_period)) item.dispatch_period = event.dispatch_period;
    if (Number.isSafeInteger(event.sequence) && event.sequence > 0) item.sequence = event.sequence;
    return [item];
  });
  if (!result.events.length) delete result.events;
  const cause = value.revision_cause;
  if (cause?.state === "untraced") result.revision_cause = { state: "untraced" };
  if (cause?.state === "exact_chain") {
    const transitions = (Array.isArray(cause.transitions) ? cause.transitions : []).slice(0, 12).map(item => {
      const normalized = {};
      for (const key of ["before_revision", "after_revision", "request_id", "operation_id", "recorded_at", "stage"]) {
        if (token(item?.[key])) normalized[key] = token(item[key]);
      }
      if (date(item?.dispatch_date)) normalized.dispatch_date = item.dispatch_date;
      if (period(item?.dispatch_period)) normalized.dispatch_period = item.dispatch_period;
      for (const key of ["actions", "source_kinds", "audit_ids", "truck_ids", "changed_fields"]) {
        normalized[key] = (Array.isArray(item?.[key]) ? item[key] : []).filter(token).slice(0, 20);
      }
      for (const key of ["audit_count", "truck_count"]) {
        if (Number.isSafeInteger(item?.[key]) && item[key] >= 0) normalized[key] = item[key];
      }
      return normalized;
    });
    // A truncated/broken chain must not appear conclusive.
    let revision = result.expected_runtime_revision;
    const complete = transitions.length > 0 && transitions.every(item => {
      if (!revision || item.before_revision !== revision || item.dispatch_date !== result.dispatch_date
        || item.dispatch_period !== result.dispatch_period) return false;
      revision = item.after_revision;
      return Boolean(revision);
    }) && revision === result.current_runtime_revision && cause.transitions.length <= 12;
    result.revision_cause = complete ? { state: "exact_chain", transitions } : { state: "untraced" };
  }
  return result;
}
